import React, { useState,useRef,useEffect } from "react";
import ReportsSection from "../components/ReportDropdown5";
import { motion, AnimatePresence } from "framer-motion";
import { useCountry } from './CountryContext'; // Adjust the path as needed

const Dropdown = ({ label, items = [], type }) => {
  const { selectedCountry } = useCountry() || {};
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(selectedCountry?.name || items[0] || "-");
    // Update selected when selectedCountry changes
    useEffect(() => {
      if (selectedCountry?.name) {
        setSelected(selectedCountry.name);
      }
    }, [selectedCountry]);
  // Scroll related states
  const contentRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(40);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startScrollTop, setStartScrollTop] = useState(0);
  const thumbRef = useRef(null);

  const icon = dropdownIcons[type] || dropdownIcons.DEFAULT;

  // Calculate thumb height based on content height
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const updateThumbHeight = () => {
      const { scrollHeight, clientHeight } = content;
      const visibleRatio = clientHeight / scrollHeight;
      setThumbHeight(Math.max(visibleRatio * clientHeight, 20));
    };

    updateThumbHeight();
    window.addEventListener("resize", updateThumbHeight);
    return () => window.removeEventListener("resize", updateThumbHeight);
  }, [isOpen, items]);

  // Handle scroll events
  const handleScroll = () => {
    const content = contentRef.current;
    if (!content) return;

    const { scrollTop, scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const scrollRatio = scrollTop / maxScrollTop;
    const trackHeight = content.clientHeight - thumbHeight;
    setScrollTop(scrollRatio * trackHeight);
  };

  // Mouse down on thumb
  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    setStartY(e.clientY);
    setStartScrollTop(contentRef.current.scrollTop);

    document.addEventListener("mousemove", handleThumbMouseMove);
    document.addEventListener("mouseup", handleThumbMouseUp);
    document.body.style.cursor = "grabbing";
  };

  // Mouse move when dragging thumb
  const handleThumbMouseMove = (e) => {
    if (!isDragging) return;

    const deltaY = e.clientY - startY;
    const content = contentRef.current;
    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const trackHeight = clientHeight - thumbHeight;

    const scrollDelta = (deltaY / trackHeight) * maxScrollTop;
    const newScrollTop = Math.min(
      Math.max(0, startScrollTop + scrollDelta),
      maxScrollTop
    );

    content.scrollTop = newScrollTop;
  };

  // Mouse up when dragging thumb
  const handleThumbMouseUp = () => {
    setIsDragging(false);
    document.removeEventListener("mousemove", handleThumbMouseMove);
    document.removeEventListener("mouseup", handleThumbMouseUp);
    document.body.style.cursor = "";
  };

  // Click on track to jump to position
  const handleTrackClick = (e) => {
    const content = contentRef.current;
    if (!content || e.target === thumbRef.current) return;

    const track = e.currentTarget;
    const trackRect = track.getBoundingClientRect();
    const clickPosition = e.clientY - trackRect.top;
    const thumbCenter = thumbHeight / 2;
    const scrollRatio =
      (clickPosition - thumbCenter) / (trackRect.height - thumbHeight);

    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    content.scrollTop = scrollRatio * maxScrollTop;
  };

  // Button scroll handlers
  const handleButtonScroll = (direction) => {
    if (contentRef.current) {
      contentRef.current.scrollBy({
        top: direction === 'up' ? -50 : 50,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="flex flex-col w-full relative">
      {/* Label */}
      <label className="md:text-[24px] font-bold text-[6px] text-black mb-1 md:mb-1">
        {label}
      </label>

      {/* Button + Icon Container */}
      <div className="flex relative z-20 w-fit">
        {/* Dropdown Button */}
        <button
          onClick={() => setIsOpen((prev) => !prev)}
          className="xl:w-[300px] lg:w-[240px] md:w-[130px] w-[40px] h-[15px] md:h-[61px] bg-[#152C24] text-white font-bold flex justify-between items-center px-3 rounded-l-[5px]"
        >
          <span className="md:text-[16px] md:px-1 text-[4px] md:mt-0 mt-[2px]">{selected}</span>
        </button>

        {/* Icon Box */}
        <div
          className="w-[20px] h-[15px] md:h-[61px] md:w-[60px] bg-[#FDBB26] text-white flex justify-center items-center rounded-r-[5px] -ml-2 z-30 cursor-pointer"
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <img
            src="https://res.cloudinary.com/dzngpgki4/image/upload/v1745014174/Expand_down_3_ggzyur.png"
            alt="expand"
            className="w-3 h-3 md:w-5 md:h-5"
          />
        </div>
      </div>
      
      <p className="md:text-[14px] font-normal mt-1 mb-1">Click to select country and view policies</p>

      {/* Dropdown List */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full mt-1 left-0 z-50 md:w-[360px] w-[180px] bg-white border border-gray-200 rounded-md shadow-lg"
          >
            <div className="w-full flex justify-end p-1">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleButtonScroll('up');
                }}
                className="p-1"
              >
                <img 
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461105/Expand_up_mflvtu.png" 
                  alt="Scroll up" 
                  className="w-3 h-3"
                />
              </button>
            </div>

            <div className="custom-scroll-area">
              <ul
                className="custom-scroll-content"
                ref={contentRef}
                onScroll={handleScroll}
              >
                {items.map((item, index) => (
                  <li
                    key={index}
                    onClick={() => {
                      setSelected(item);
                      setIsOpen(false);
                    }}
                    className="md:p-3 p-1 md:mt-1 md:text-[18px] text-[10px] rounded-md hover:bg-[#afafaf] cursor-pointer flex items-center"
                  >
                    <span className="text-black font-bold">{item}</span>
                  </li>
                ))}
              </ul>
              <div 
                className="custom-scroll-track" 
                onClick={handleTrackClick}
              >
                <div
                  ref={thumbRef}
                  className="custom-scroll-thumb"
                  style={{
                    height: `${thumbHeight}px`,
                    top: `${scrollTop}px`,
                    cursor: isDragging ? "grabbing" : "grab",
                  }}
                  onMouseDown={handleThumbMouseDown}
                ></div>
              </div>
            </div>

            <div className="w-full flex justify-end p-1">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleButtonScroll('down');
                }}
                className="p-1"
              >
                <img 
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461085/Expand_down_vbf4bb.png" 
                  alt="Scroll down" 
                  className="w-3 h-3"
                />
              </button>
            </div>

            <style jsx>{`
              .custom-scroll-area {
                position: relative;
                max-height: 200px;
                display: flex;
                overflow: hidden;
              }

              .custom-scroll-content {
                flex: 1;
                overflow-y: scroll;
                scrollbar-width: none;
                -ms-overflow-style: none;
                padding-right: 4px;
              }

              .custom-scroll-content::-webkit-scrollbar {
                display: none;
              }

              .custom-scroll-track {
                width: 14px;
                background: #e7ebeb;
                border-radius: 3px;
                position: relative;
                margin-left: 4px;
                cursor: pointer;
              }

              .custom-scroll-thumb {
                width: 100%;
                background: #979797;
                border-radius: 3px;
                position: absolute;
                right: 0;
                transition: background 0.2s;
              }

              .custom-scroll-thumb:hover {
                background: #444;
              }

              @media (max-width: 768px) {
                .custom-scroll-track {
                  width: 4px;
                  margin-left: 2px;
                }
                .custom-scroll-area {
                  max-height: 150px;
                }
              }
            `}</style>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};


const dropdownItems = {
  COUNTRY: [
    "SOUTH AFRICA",
    "Botswana",
    "Cameroon",
    "Congo Democratic Republic (DRC)",
    "Cote-divorie",
    "Eswatini (Swaziland)",
    "Ethiopia",
    "Ghana",
    "Kenya",
    "Lesotho",
    "Malawi",
    "Mozambique",
    "Namibia",
    "Nigeria",
    "Rwanda",
    "Senegal",
    "Sierra Leone",
    "Somalia",
    "South Africa",
    "South Sudan",
    "Tanzania",
    "Uganda",
    "Zambia",
    "Zimbabwe",
  ],
  REGION: [
    "GAUTENG",
    "EASTERN CAPE",
    "LIMPOPO",
    "KWAZULU-NATAL",
    "MPUMALANGA",
    "WESTERN CAPE",
    "NORTH WEST",
    "NORTHERN CAPE",
    "FREE STATE"
  ],
  PROJECT: ["ABBATOIR"],
  CROP: [
    "MAIZE",
    "Apples",
    "Avocado",
    "Bananas",
    "Beans",
    "Cabbages",
    "Kale",
    "Mangoes",
    "Onions",
    "Passion Fruits",
    "Papaya",
    "Potatoes",
    "Strawberries",
    "Tomatoes",
    "Watermelon",
  ],
  LIVESTOCK: ["POULTRY"],
  DATA: ["PRODUCTION", "Revenue", "Inventory"],
};

const dropdownIcons = {
  COUNTRY: "https://res.cloudinary.com/dzngpgki4/image/upload/v1744492909/Pin_alt_ioedjs.png",
  REGION: "https://res.cloudinary.com/dzngpgki4/image/upload/v1744493134/Order_ytgysy.png",
  CROP: "https://res.cloudinary.com/dzngpgki4/image/upload/v1744493134/Order_ytgysy.png",
  DATA: "https://res.cloudinary.com/dzngpgki4/image/upload/v1744493134/Order_ytgysy.png",
  PROJECT: "https://res.cloudinary.com/dzngpgki4/image/upload/v1744493133/image_fdtjzb.png",
  LIVESTOCK: "https://res.cloudinary.com/dzngpgki4/image/upload/v1744493189/image_34_zwohkn.png",
  DEFAULT: "https://res.cloudinary.com/dzngpgki4/image/upload/v1744493134/Order_ytgysy.png"
};

const Policy = ({ dropdownType = "COUNTRY" }) => {
  const { selectedCountry } = useCountry() || { selectedCountry: { name: "Namibia" } };
  const [reportSelections, setReportSelections] = useState({
    INPUT_PRODUCTION_POLICIES: "-",
    LAND_TENURE_POLICIES: "-",
    MARKET_TRADE_POLICIES: "-",
    AGRICULTURE_FINANCE: "-",
    RESEARCH_INNOVATION: "-",
    FOOD_SECURITY: "-",
    CLIMATE_CHANGE: "-",
    GENDER_YOUTH: "-",
    INSTITUTIONAL_REFORMS: "-",
    REGIONAL_FRAMEWORKS: "-"
  });

  const handleReportDropdownChange = (type, value) => {
    setReportSelections(prev => ({
      ...prev,
      [type]: value,
      ...(type === 'COUNTRY' && { 
        REGION: "-",
        DISTRICT: "-",
        MUNICIPALITY: "-" 
      })
    }));
  };

  const handleSaveFromDropdown = (savedData) => {
    setReportSelections(savedData);
  };

  const SMMEsOptions = [
    {
      heading: "Smallholder Commercial Farms",
      items: [{
        label: "Small-scale farms operating for profit. Use improved inputs...",
        value: "ESC"
      }]
    },
    {
      heading: "Medium-Scale Farms",
      items: [{
        label: "Larger landholding than smallholders (e.g., 10–100 hectares)...",
        value: "EMS"
      }]
    },
    {
      heading: "Farmer Cooperatives / Producer Organizations",
      items: [{
        label: "Groups of farmers who pool resources to gain market power...",
        value: "EFC"
      }]
    },
    {
      heading: "Contract Farming SMEs",
      items: [{
        label: "Farmers or farm groups under formal agreements to supply...",
        value: "ECF"
      }]
    },
    {
      heading: "Niche or Specialized Farms",
      items: [{
        label: "Farms focused on high-value or specialized production...",
        value: "ENS"
      }]
    },
    {
      heading: "Agri-Entrepreneurs with Integrated Operations",
      items: [{
        label: "These SMEs farm and also manage part of the value chain...",
        value: "EAE"
      }]
    },
    {
      heading: "Youth or Women-led Farming Startups",
      items: [{
        label: "Often tech-savvy and innovative. Use climate-smart or...",
        value: "EYW"
      }]
    }
  ];
  const handleDownload = () => {
    const pdfUrl = 'https://www.nafsan.org/wp-content/uploads/2020/04/2014-namibia-food-safety-policy.pdf';
    const fileName = '2014-namibia-food-safety-policy.pdf';
    
    // Create a temporary anchor element
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = fileName;
    
    // Set the download attribute to force download
    link.setAttribute('download', fileName);
    
    // Append to the body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const validDropdownType = Object.keys(dropdownItems).includes(dropdownType) 
    ? dropdownType 
    : "COUNTRY";

  return (
    <div className='bg-white'>
      <div className='md:px-24 px-4 pt-5'>
        <div className="flex justify-between items-center">
          <h2 className="text-[16px] md:text-[36px] font-bold tracking-wide text-[#152C24]">
            POLICY
          </h2>
        </div>
        
        <div className="flex items-center justify-between ">
          <img 
            src="https://res.cloudinary.com/dzngpgki4/image/upload/v1748292859/FarmNet_Logo_Green_4_eulwfl.png" 
            alt="FarmNet Logo" 
            className="md:w-[250px] md:h-[40px] w-[120px] h-[20px] md:mt-5 mt-2"
          />
          <div className="flex items-center justify-end">
          <Dropdown
            label="Country"
            items={dropdownItems.COUNTRY}
            type="COUNTRY"
          />
          </div>
        </div>

        <div>
          <h1 className='md:text-[32px] text-[14px] font-normal md:mt-5 md:mb-5 mb-3 mt-3 leading-tight'>
            Agricultural policies in Africa are critical for addressing food security, rural development, climate change, and economic transformation. These policies often vary by country but tend to focus on common themes. Here's a categorized list of important types of agricultural policies across Africa:
          </h1>
        </div>

        <div>
        <div>
  <h1 className='text-[#152C24] text-[20px] md:text-[32px] font-bold md:mt-4 mb-3'>
    {selectedCountry.name.toUpperCase()}
  </h1>
</div>
        </div>

        <div className="w-full md:mb-4 mb-3">
          <div className="relative">
            <input
              placeholder="Search"
              type="text"
              className="w-full h-[40px] md:h-[61px] text-[#979797] text-[14px] md:text-[25px] font-bold p-2 md:p-3 pr-10 md:px-6 px-3 border border-[#C4C4C4] rounded-full md:rounded-md focus:outline-none focus:ring-2 focus:ring-green-600"
            />
            <img
              src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744490602/Search_k7vxkr.png"
              alt="Search Icon"
              className="w-[18px] h-[18px] md:h-[24px] md:w-[24px] absolute right-3 md:right-5 top-1/2 transform -translate-y-1/2"
            />
          </div>
          <p className="text-[12px] md:text-[16px] mb-2 mt-2">Search by entering reference or unique identification number.</p>
        </div>

        <ReportsSection
          values={reportSelections}
          onChange={handleReportDropdownChange}
          onSave={handleSaveFromDropdown}
          smmesOptions={SMMEsOptions}
        />

        <h2 className="text-[18px] md:text-[36px] text-[#152C24] font-bold mt-4">
          <span className="text-[#979797]">POLICIES  | </span>Fertilizer subsidies
        </h2>
        
        <h2 className="text-[18px] md:text-[36px] md:mt-5 mt-3 text-[#152C24] font-bold">
          Fertilizer Subsidy Programs
        </h2>
        
        <div>
          <h1 className='md:text-[32px] text-[14px] font-normal md:mt-5 md:mb-5 mb-3 mt-3 leading-tight'>
            Namibia has implemented fertilizer subsidy initiatives as part of its broader agricultural support programs, particularly under the Ministry of Agriculture, Water and Land Reform (MAWLR). These policies aim to boost crop production, especially among smallholder and communal farmers, in a country where arid conditions and poor soil fertility limit agricultural productivity.
          </h1>
        </div>

        {/* PDF Viewer and Download Button Section */}
        <div className="mt-6 mb-6 w-full md:max-w-[250px]">
          {/* PDF Viewer */}
          <div className="mb-4 w-full" style={{
            height: "300px",
            borderRadius: '5px',
            borderWidth: '1px',
            borderColor: '#D9D9D9',
            borderStyle: 'solid',
            overflow: 'hidden'
          }}>
            <iframe 
              src="https://www.nafsan.org/wp-content/uploads/2020/04/2014-namibia-food-safety-policy.pdf" 
              width="100%" 
              height="100%"
              style={{ border: 'none' }}
              title="Namibia Food Safety Policy PDF"
            >
              <p>Your browser does not support PDFs. <a href="https://www.nafsan.org/wp-content/uploads/2020/04/2014-namibia-food-safety-policy.pdf">Download the PDF</a> instead.</p>
            </iframe>
          </div>
          
          <div className="flex justify-end">
            {/* Download Button */}
            <button 
              onClick={handleDownload}
              className="px-4 py-2 text-[14px] md:text-[16px] flex items-center gap-2 rounded-md transition-colors"
            >
              Download
              <img src="https://res.cloudinary.com/dzngpgki4/image/upload/v1749155706/Import_duotone_line_gjd1px.png" alt="download" className="w-[24px] h-[24px] md:w-[36px] md:h-[36px]"/>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Policy;